import {
  collectLayoutFieldNames,
  isLayoutScopedFieldKey,
} from '@/common/utils/layoutScopedClientFields'
import { setType } from '@/common/utils/typedValueSerializer'

type TypedField = { type?: string; value?: unknown }

function readUniDataTableRows(entry: unknown): unknown[] {
  if (!entry || typeof entry !== 'object') return []
  const e = entry as { type?: string; value?: { rows?: unknown[] }; rows?: unknown[] }
  if (Array.isArray(e.value?.rows)) return e.value!.rows!
  if (Array.isArray(e.rows)) return e.rows
  return []
}

function readCell(row: unknown, column: string): unknown {
  if (!row || typeof row !== 'object') return undefined
  const r = row as Record<string, unknown>
  const fields = r.fields as Record<string, { value?: unknown }> | undefined
  if (fields?.[column] && typeof fields[column] === 'object' && 'value' in fields[column]) {
    return fields[column].value
  }
  return r[column]
}

function writeCell(row: Record<string, unknown>, column: string, value: unknown): void {
  const fields =
    row.fields && typeof row.fields === 'object' && !Array.isArray(row.fields)
      ? (row.fields as Record<string, { value: unknown; type: string }>)
      : {}
  fields[column] = setType(value)
  row.fields = fields
}

function isEmptyCell(value: unknown): boolean {
  return value === '' || value === null || value === undefined
}

function isUniDataTableEntry(entry: unknown): boolean {
  return Boolean(entry && typeof entry === 'object' && String((entry as TypedField).type ?? '') === 'UniDataTable')
}

/** UniDataTable wire-форма string[] / TypedArray: колонка `self`. */
export function extractSelfStringListValues(entry: unknown): string[] {
  const rows = readUniDataTableRows(entry)
  const out: string[] = []
  for (const row of rows) {
    const selfVal = readCell(row, 'self')
    if (typeof selfVal === 'string' && selfVal.trim() !== '') {
      out.push(selfVal)
    } else if (typeof selfVal === 'string') {
      out.push('')
    }
  }
  return out
}

export function isSelfOnlyStringListTable(entry: unknown): boolean {
  if (!isUniDataTableEntry(entry)) return false
  const e = entry as { value?: { columns?: unknown } }
  const columns = e.value?.columns
  if (Array.isArray(columns) && columns.length === 1 && columns[0] === 'self') {
    return true
  }
  const rows = readUniDataTableRows(entry)
  if (rows.length === 0) return false
  return rows.every((row) => {
    if (!row || typeof row !== 'object') return false
    const fields = (row as { fields?: Record<string, unknown> }).fields
    if (!fields || typeof fields !== 'object') return false
    const keys = Object.keys(fields)
    return keys.length === 1 && keys[0] === 'self'
  })
}

/**
 * Начальный currentData (createClientDataUniData): только ключи layout + identity / platform suffix.
 * Накопленный UniData после onClick / procedure этим не резать (WUI-507).
 */
export function sanitizeOutboundActionClientDataFields(
  fields: Record<string, unknown>,
  options: {
    formRoot?: unknown
    layoutElementNames?: Set<string>
  } = {},
): Record<string, unknown> {
  const layoutNames =
    options.layoutElementNames ??
    (options.formRoot != null ? collectLayoutFieldNames(options.formRoot) : new Set<string>())
  if (layoutNames.size === 0) {
    return fields
  }

  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(fields)) {
    if (!isLayoutScopedFieldKey(key, layoutNames)) {
      // Document TypedArray (колонка self) нужен workflow `$("each")(websiteUrl)` — не отбрасывать.
      if (isSelfOnlyStringListTable(value)) {
        out[key] = value
        continue
      }
      continue
    }
    out[key] = value
  }
  return out
}

/**
 * Таблица с пустым `url` + соседний self-list → заполнить url по индексу.
 * Без прикладных имён полей: только wire-shape (колонка url + TypedArray self).
 */
export function fillEmptyUrlCellsFromSelfStringLists(
  fields: Record<string, unknown>,
  extraSourceFields?: Record<string, unknown> | null,
): string[] {
  if (!fields || typeof fields !== 'object') return []

  const selfLists: string[][] = []
  const collectFrom = (map: Record<string, unknown> | null | undefined) => {
    if (!map || typeof map !== 'object') return
    for (const value of Object.values(map)) {
      if (!isSelfOnlyStringListTable(value)) continue
      const list = extractSelfStringListValues(value)
      if (list.some((s) => s.trim() !== '')) {
        selfLists.push(list)
      }
    }
  }
  collectFrom(fields)
  collectFrom(extraSourceFields)
  if (selfLists.length === 0) return []

  const changed: string[] = []
  for (const [key, value] of Object.entries(fields)) {
    if (!isUniDataTableEntry(value)) continue
    const rows = readUniDataTableRows(value)
    if (rows.length === 0) continue

    const hasUrlColumn = rows.some((row) => {
      if (!row || typeof row !== 'object') return false
      const fieldsMap = (row as { fields?: Record<string, unknown> }).fields
      return Boolean(fieldsMap && Object.prototype.hasOwnProperty.call(fieldsMap, 'url'))
    })
    if (!hasUrlColumn) continue

    const source = selfLists.find((list) => list.length === rows.length) ?? selfLists[0]
    if (!source?.length) continue

    let rowChanged = false
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      if (!row || typeof row !== 'object') continue
      const current = readCell(row, 'url')
      if (!isEmptyCell(current)) continue
      const fill = source[i] ?? source[0]
      if (typeof fill !== 'string' || fill.trim() === '') continue
      writeCell(row as Record<string, unknown>, 'url', fill)
      rowChanged = true
    }
    if (rowChanged) {
      changed.push(key)
      const entry = value as { type?: string; value?: { rows?: unknown[] } }
      if (entry.value && typeof entry.value === 'object') {
        entry.value.rows = rows
      }
    }
  }
  return changed
}
